import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  Container,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Toolbar,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import { useAuth } from '../contexts/AuthContext';
import BrandMark from './BrandMark';

interface NavItem {
  label: string;
  href: string;
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Início', href: '/#top' },
  { label: 'Planos para Médicos', href: '/#para-quem' },
  { label: 'Como Funciona', href: '/#como-funciona' },
  { label: 'Benefícios', href: '/#beneficios' },
  { label: 'Para Clínicas', href: '/#clinicas' },
  { label: 'Dúvidas', href: '/#faq' },
];

const LandingNavbar: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useAuth();

  const handleDrawerToggle = () => setMobileOpen((prev) => !prev);

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        bgcolor: 'rgba(247, 249, 248, 0.85)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid',
        borderColor: 'divider',
        color: 'text.primary',
      }}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ height: { xs: 72, md: 84 } }}>
          <Box
            component="a"
            href="/#top"
            aria-label="MedPrime Benefícios — página inicial"
            sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, textDecoration: 'none' }}
          >
            <BrandMark size={isMobile ? 'small' : 'medium'} />
          </Box>

          {isMobile ? (
            <IconButton
              color="inherit"
              aria-label="Abrir menu de navegação"
              edge="start"
              onClick={handleDrawerToggle}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 3 }}>
                {NAV_ITEMS.map((item) => (
                  <Button
                    key={item.label}
                    component="a"
                    href={item.href}
                    sx={{
                      color: 'text.primary',
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      px: 1.5,
                      '&:hover': { bgcolor: 'rgba(14, 75, 79, 0.06)' },
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Box>

              <Button
                component={RouterLink}
                to={user ? '/admin' : '/login'}
                startIcon={<PersonOutlineRoundedIcon />}
                sx={{
                  color: 'text.secondary',
                  fontWeight: 500,
                  fontSize: '0.85rem',
                  mr: 1.5,
                  '&:hover': { bgcolor: 'rgba(14, 75, 79, 0.06)' },
                }}
              >
                {user ? 'Área do Consultor' : 'Acesso Consultor'}
              </Button>

              <Button
                component="a"
                href="/#cotacao"
                variant="contained"
                color="primary"
                sx={{
                  py: 1,
                  px: 3,
                  boxShadow: '0 4px 14px rgba(14, 75, 79, 0.25)',
                }}
              >
                Solicitar Cotação
              </Button>
            </>
          )}
        </Toolbar>
      </Container>

      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 280, bgcolor: 'background.default' },
        }}
      >
        <Box sx={{ p: 3 }}>
          <BrandMark size="small" />
        </Box>
        <Divider />
        <List sx={{ px: 1, py: 2 }}>
          {NAV_ITEMS.map((item) => (
            <ListItemButton
              key={item.label}
              component="a"
              href={item.href}
              onClick={handleDrawerToggle}
              sx={{ borderRadius: 2, mb: 0.5 }}
            >
              <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 500 }} />
            </ListItemButton>
          ))}
          <Divider sx={{ my: 1.5 }} />
          <ListItemButton
            component={RouterLink}
            to={user ? '/admin' : '/login'}
            onClick={handleDrawerToggle}
            sx={{ borderRadius: 2 }}
          >
            <ListItemText
              primary={user ? 'Área do Consultor' : 'Acesso Consultor'}
              primaryTypographyProps={{ fontWeight: 500, color: 'text.secondary' }}
            />
          </ListItemButton>
        </List>
        <Box sx={{ px: 2, pb: 3 }}>
          <Button
            component="a"
            href="/#cotacao"
            onClick={handleDrawerToggle}
            fullWidth
            variant="contained"
            color="primary"
            size="large"
          >
            Solicitar Cotação
          </Button>
        </Box>
      </Drawer>
    </AppBar>
  );
};

export default LandingNavbar;
